import { useSnackbarNotification } from '@jupiterone/jupiter-web-toolkit/src/components/notifications/SnackbarNotificationContext';
import { useSession } from '@jupiterone/jupiter-web-toolkit/src/api/session';
import { J1_ACCOUNT_HEADER } from '@jupiterone/jupiter-web-toolkit/src/constants';

/*
  This wrapper around fetch is intended to be used to simplify the 
  authenticated fetch calls. It will append the access token as well as 
  the account id (when available). We want to isolate the places we set 
  the auth headers to make it easy to update the auth flow. This also lets 
  us set a default response to fetch errors. By default we assume we are 
  dealing with a json return body with an error code and we assume failed 
  fetches include a non 200 (response.ok). When we get an error we display 
  the content to the user via the snackbar notification. This also uploads
  relevant info to sentry. 

  Note: If a non 200 error code should not be treated as an error the hooks 
  should include a false boolean argument: 
  `const { authFetch } = useJ1AuthFetch({snackbarNotification: false})`
  This lets the callers of authFetch handle the error response as appropriate, 
  e.g. bad user input that should be a snackbar warning not error.
*/
type AuthFetchProps = {
  snackbarNotification: Boolean;
};
const AUTH_FETCH_DEFAULT_OBJECT_PROPS = { snackbarNotification: true };
const useJ1AuthFetch = ({
  snackbarNotification = true,
}: AuthFetchProps = AUTH_FETCH_DEFAULT_OBJECT_PROPS) => {
  // Temporary useSession until we switch to functional access auth
  const session = useSession();
  const notification = useSnackbarNotification();

  const notificationsWrappedFetch = async (
    url: string,
    options?: RequestInit
  ) => {
    try {
      const response = await fetch(url, options);
      if (response.ok) return response;
      // In cases where we are not using application/json data will be `undefined`
      // AFAIK there is no standard for error responses when not using
      // application/json and those case will have to be handled manually ~Sivli
      const data = await response.json?.();
      throw Error(
        data?.error?.message ||
          `${response.status}: ${
            response.statusText
              ? response.statusText
              : 'API Error, no reason returned'
          }`
      );
    } catch (error) {
      notification.error({
        error: error,
        message: error.message,
        // This will go to Sentry which should dramatically ease debugging
        context: { url },
      });
      return Promise.reject(error);
    }
  };

  const authFetch = async (url: string, options?: RequestInit) => {
    if (!session) throw Error('Session not defined');
    const { accessToken, account } = session;
    if (!accessToken) throw Error('User no longer signed in');
    const headers = {
      ...options?.headers,
      Authorization: `Bearer ${accessToken}`,
    };
    if (account?.id) (headers as any)[J1_ACCOUNT_HEADER] = account.id;
    if (snackbarNotification) {
      return await notificationsWrappedFetch(url, {
        ...options,
        headers,
      });
    } else {
      return await fetch(url, {
        ...options,
        headers,
      });
    }
  };

  // Also expose an "unauthorized" fetch wrapper w/ notifications
  // for when we don't want to attach the auth header(s)
  const unauthFetch = async (url: string, options?: RequestInit) => {
    if (snackbarNotification) {
      return await notificationsWrappedFetch(url, options);
    } else {
      return await fetch(url, options);
    }
  };

  return {
    authFetch,
    unauthFetch,
  };
};
export type useJ1AuthFetchReturn = ReturnType<typeof useJ1AuthFetch>;
export default useJ1AuthFetch;
